import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import {
	fromMillisecondsToMinutes,
	fromMinutesToHours,
	fromSecondsToMilliseconds,
	fromSecondsToMinutes,
} from "@obg/ngx.global";
import { PlatformService } from "@obg/ngx.global.evo";
import { EMPTY, forkJoin, of } from "rxjs";
import { catchError, delay, map, switchMap, takeUntil } from "rxjs/operators";

import { AppError } from "../../app-error/app-error.model";
import { AuthSelector } from "../../auth/auth.selector";
import { SESSION_ACTION_TYPE } from "../../auth/session.action";
import { TimeToLiveResponse } from "../../auth/session.model";
import { CoreState } from "../../state";
import { WalletConfigService } from "../../wallet/wallet.config";
import { BonusContext, Transaction } from "../../wallet/wallet.model";
import { WalletSelector } from "../../wallet/wallet.selector";
import { WalletService } from "../../wallet/wallet.service";
import { ResponsibleGamingConfigService } from "../responsible-gaming.config";

import { SessionSummaryAction } from "./session-summary.action";
import { SECONDS_BEFORE_EXPIRY } from "./session-summary.const";
import { SessionSummary } from "./session-summary.model";

/**
 * @deprecated [fabric-deprecated] - Use SessionSummaryService from app-responsible-gaming instead
 */
@Injectable()
export class SessionSummaryEffect {
	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetSessionSummary instead
	 */
	fetch$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SessionSummaryAction.fetch),
			map((x) => x.logout),
			concatLatestFrom(() => [
				this.config.getSessionSummaryEnabled$(),
				this.store.select(AuthSelector.getSessionElapsedTimeInMillis),
				this.store.select(this.walletSelector.getBalance()),
			]),
			switchMap(([logout, sessionSummaryEnabled, millis, balance]) =>
				sessionSummaryEnabled
					? forkJoin({
							/* eslint-disable rxjs/finnish */
							betWinSummary: this.walletService.getBetWinSummary(
								Math.ceil(fromMillisecondsToMinutes(millis ?? 0))
							),
							products: this.findProductsUsed(millis ?? 0),
							/* eslint-enable rxjs/finnish */
						}).pipe(
							map((result) => {
								const minutes = fromMillisecondsToMinutes(millis ?? 0);
								const totalSessionDurationInMinutes =
									minutes + (logout ? 0 : fromSecondsToMinutes(SECONDS_BEFORE_EXPIRY));
								return {
									totalBets: result.betWinSummary.totalBets,
									currencyCode: result.betWinSummary.currencyCode,
									netWinning: `${
										result.betWinSummary.netWinning > 0 ? "+" : ""
									}${result.betWinSummary.netWinning.toString()}`,
									balance: balance?.totalAmount ?? 0.0,
									sessionDurationHours: Math.floor(fromMinutesToHours(totalSessionDurationInMinutes)),
									sessionDurationMinutes: Math.ceil(totalSessionDurationInMinutes % 60),
									productsUsed: result.products,
								} as SessionSummary;
							}),
							map((sessionSummary) =>
								logout
									? SessionSummaryAction.fetchBeforeLogoutSuccess(sessionSummary)
									: SessionSummaryAction.fetchSuccess(sessionSummary)
							),
							catchError((error: AppError) =>
								logout
									? of(SessionSummaryAction.fetchBeforeLogoutFailure(error))
									: of(SessionSummaryAction.fetchFailure(error))
							)
						)
					: logout
						? of(SessionSummaryAction.fetchBeforeLogoutCancel())
						: EMPTY
			)
		)
	);

	/**
	 * @deprecated [fabric-deprecated] - Use ResponsibleGamingAppCommand.GetSessionSummary instead
	 */
	fetchBeforeSessionExpiry$ = this.platformService.forClient(() =>
		createEffect(() =>
			this.actions$.pipe(
				ofType(
					SESSION_ACTION_TYPE.loginSuccess,
					SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
					SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
					SESSION_ACTION_TYPE.loginWithTokenSuccess,
					SESSION_ACTION_TYPE.verifySuccess,
					SESSION_ACTION_TYPE.keepAliveSuccess
				),
				map((x: ActionWithPayload<TimeToLiveResponse>) => x.payload.timeToLiveSeconds),
				switchMap((seconds) =>
					of(SessionSummaryAction.fetch()).pipe(
						delay(
							fromSecondsToMilliseconds(
								seconds > SECONDS_BEFORE_EXPIRY ? seconds - SECONDS_BEFORE_EXPIRY : 0
							)
						),
						takeUntil(
							this.actions$.pipe(
								ofType(SessionSummaryAction.fetchSuccess, SessionSummaryAction.fetchFailure)
							)
						)
					)
				)
			)
		)
	);

	constructor(
		private actions$: Actions,
		private store: Store<CoreState>,
		private walletService: WalletService,
		private walletSelector: WalletSelector,
		private config: ResponsibleGamingConfigService,
		private platformService: PlatformService,
		private walletConfigService: WalletConfigService
	) {}

	private findProductsUsed(millis: number) {
		const sessionStart = new Date().getTime() - millis;
		return this.walletService
			.getTransactions({
				from: new Date(sessionStart).toISOString(),
				to: new Date().toISOString(),
				pageNumber: 0,
				pageSize: 100,
				shouldCombineTax: true,
			})
			.pipe(
				map((transaction: Transaction) =>
					transaction.transactions
						?.map((transactionDetail) => transactionDetail.bonusContextName)
						.filter((bonusContexts) => bonusContexts)
						.map(
							(ctx) =>
								this.walletConfigService
									.getTransactionBonusContexts()
									.find((context) => context.bonusContext.includes(ctx as BonusContext))?.productType
						)
						.filter((product, index, list) => list.indexOf(product) === index)
				),
				catchError(() => of([]))
			);
	}
}
